import React from 'react'
import get from 'lodash/fp/get'
import invariant from 'invariant'
import { FormattedMessage } from '@dragonraider5/react-intl'
import defaultMessages from './messages'

/**
 * get the validation message descriptor from provided messages
 * or from default messages
 *
 * @throws {InvariantError} if no message in provided messages and in default messages
 * @param {Object} validation - validation key to show messages
 * @param {Object} [messages={}] - custom dictionary of messages
 * @return {Object} message descriptor for validation
 */
export const getValidationMessage = (validation, messages = {}) => {
  const message = messages[validation] || defaultMessages[validation]

  invariant(
    message != null,
    `Could not find ${validation} in provided validation messages or defaultMessages, ` +
    `please make sure to include ${validation} in provided messages`
  )

  return message
}

/**
 * check if a field has to show error
 *
 * @param {String} name - name of the field
 * @param {Object} props - component props
 * @param {Object<String, String>} props.errors - dictionary of fields with errors
 * @param {Object<String, Boolean>} props.touched - dictionary of fields that has been touched
 * @return {Boolean} true if field has error and has been touched
 */
export const showFieldError = (name, { errors, touched }) => !!get(name, errors) && get(name, touched)

/**
 * check if field has to show error and return FormattedMessage in case it
 *
 * @throws {InvariantError} if no message in provided messages and in default messages
 * @param {String} name - name of the field
 * @param {Object} props - component props
 * @param {Object<String, String>} props.errors - dictionary of fields with errors
 * @param {Object<String, Boolean>} props.touched - dictionary of fields that has been touched
 * @param {Object} [messages={}] message descriptor for validation
 * @return {Element|null} FormattedMessage if has to show error
 */
export const renderValidation = (name, { errors, touched }, messages = {}) =>
  showFieldError(name, { errors, touched })
    ? (
      <FormattedMessage
        {...getValidationMessage(get(name, errors), messages)}
      />
    ) : null
